// src/domains/sales/deals/forms/create-form/sales-deals-create-form.jsx

import { InputField, InputSelectOptions, ToggleField } from 'components/molecules'
import { FieldArray, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { Alert, FormText, Button, Input, Label } from 'reactstrap'
import SalesDealsSchema from './sales-deals-schema'
import UserSelectSearchField from '../../organisms/user-select-search-field'
import { SALES_METHOD } from '../../constants'
import ReactAsyncMultiselectField from 'components/molecules/react-async-multiselect-field'
import { useState } from 'react'
import SalesDealsApi from 'api/admins/sales-deals-api'
import CourierSelectSearchField from '../../organisms/courier-select-search-field'
import PaymentMethodSelectSearchField from '../../organisms/payment-method-select-search-field'
import PhoneCodeSelectSearchField from '../../organisms/phone-code-select-search-field'
import UserAddressPinpoint from '../../organisms/address-pinpoint'
import { useLoadScript } from '@react-google-maps/api'
import DateInputField from 'components/molecules/date-input-field'
import StringUtil from 'utils/string-util'
import LeadSourceSelectSearchField from '../../organisms/lead-source-select-search-field'
import SalesAliasSelectSearchField from '../../organisms/sales-alias-select-search-field'
import SalesNameSelectSearchField from '../../organisms/sales-name-select-search-field'

const PLACE_LIBRARIES = [ 'places' ]

const propTypes = {
  onSubmitForm            : PropTypes.func.isRequired,
  checkUserEmailIsUnique  : PropTypes.func,
  checkUserPhoneIsUnique  : PropTypes.func,
}

const splitFullName = (fullName = '') => {
  const cleaned = (fullName || '').trim().replace(/\s+/g, ' ')
  if(!cleaned) return { first: '', last: '' }
  const parts = cleaned.split(' ')
  const first = parts.shift() || ''
  const last  = parts.join(' ')
  return { first, last }
}

const buildFullName = (firstName, lastName) => {
  const first = (firstName || '').trim()
  const last  = (lastName || '').trim()
  return [ first, last ].filter(Boolean).join(' ').trim()
}

const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const isItemComplete = (item) => {
  const n = toNumberOrNull(item?.selling_price)
  return n !== null && n > 0
}

const SalesDealsCreateForm = ({
  onSubmitForm,
  checkUserEmailIsUnique = () => {},
  checkUserPhoneIsUnique = () => {},
}) => {
  const initialValues = {
    // CUSTOMER
    use_existing_user: true,
    edit_existing_user: false,
    hide_existing_user_detail: true,

    user_id: null,
    email: null,
    password: null,
    birthdate: null,

    // split name UI
    first_name: '',
    last_name: '',
    // legacy full name
    name: '',

    phone: null,
    phone_code: null,
    instagram_account: null,
    lead_source: null,

    complete_address: null,
    recipient_name: null,
    recipient_number: null,
    instruction: null,
    latitude: null,
    longitude: null,

    // SALES
    sales_name_in_invoice: null,
    deal_by: null,
    assist_by: null,

    // DEAL
    sales_method: null,

    // ITEMS
    product_search: [],  // single-select (stored as array b/c component is multi)
    product_ids: [],     // added products (display-only)
    items: [],           // payload

    // SHIPMENT
    pickup_at: null,
    courier: null,

    // ORDER
    voucher_code: null,
    include_invoice: true,
    include_polaroid: false,
    include_love_letter: false,
    note: '',
    down_payment: null,

    // PAYMENT (last)
    payment_method: null,
  }

  const { isLoaded: gmapLoaded } = useLoadScript({
    googleMapsApiKey : process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries        : PLACE_LIBRARIES
  })

  const [ showPinpoint, setShowPinpoint ] = useState(false)
  const [ productSearchQuery, setProductSearchQuery ] = useState("")

  const loadOptions = async () => {
    const result = await SalesDealsApi.searchLiveProducts({query: productSearchQuery})
      .then((response) => response.data.products)
    return result
  }

  // label: SKU - condition - spec
  const getOptionLabel = (option) => {
    const sku = option?.sku
    const condition = option?.condition_name || option?.condition || ''
    const spec = option?.spec || option?.p_size || option?.name || ''
    return [sku, condition, spec].filter(Boolean).join(' - ')
  }

  const syncFullNameIntoLegacyName = (formikBag, firstName, lastName) => {
    formikBag.setFieldValue('name', buildFullName(firstName, lastName))
  }

  const addSelectedProductAsItem = (formikBag) => {
    const selected = (formikBag.values.product_search || [])[0]
    if(!selected) return

    const alreadyAdded = (formikBag.values.product_ids || []).some(p => p.id === selected.id)
    if(alreadyAdded){
      alert(`Item sudah ditambahkan: ${selected.sku}`)
      return
    }

    // default Final Price = product.selling_price, fallback to default_price
    const defaultFinalPrice =
      (selected.selling_price !== null && selected.selling_price !== undefined && selected.selling_price !== '')
        ? selected.selling_price
        : selected.default_price

    const newItem = {
      product_id: selected.id,
      selling_price: defaultFinalPrice || '',
      authenticator: '',
      authenticity_check_note: '',
      include_authenticity_check: false,
      note: '',
    }

    formikBag.setFieldValue('product_ids', [ ...(formikBag.values.product_ids || []), selected ])
    formikBag.setFieldValue('items', [ ...(formikBag.values.items || []), newItem ])

    // clear search
    formikBag.setFieldValue('product_search', [])
    setProductSearchQuery("")
  }

  const BlackSectionHeader = ({ title, subtitle }) => (
    <div
      style={{
        background: "#000",
        color: "#fff",
        padding: "12px 16px",
        borderRadius: 6,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, letterSpacing: 0.3 }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
          {subtitle}
        </div>
      )}
    </div>
  )

  const SectionTitle = ({ title, subtitle }) => (
    <div style={{ marginBottom: 10 }}>
      <h4 style={{ marginBottom: 6 }}>{title}</h4>
      {subtitle && <FormText style={{ marginTop: 0 }}>{subtitle}</FormText>}
    </div>
  )

  return (
    <Formik
      initialValues={ initialValues }
      validationSchema={ SalesDealsSchema }
      onSubmit={ (values, formikBag) => onSubmitForm(values, formikBag) }
    >
      {(formikBag) => {
        const addNewUser = !formikBag.values.use_existing_user

        const items = formikBag.values.items || []
        const canAddNewItem = items.length === 0 || items.every(isItemComplete)

        const allItemsComplete = items.length > 0 && items.every(isItemComplete)
        const hasAtLeastOneItem = items.length > 0

        const selectedToAdd = (formikBag.values.product_search || [])[0]
        const canClickAdd = !!selectedToAdd && canAddNewItem

        const hasSelectedButNotAdded =
          (formikBag.values.product_search || []).length > 0 &&
          !formikBag.values.items?.some(i => i.product_id === selectedToAdd?.id)

        return (
          <Form onSubmit={(e) => e.preventDefault()}>

            {/* =========================
                QUICK GUIDE
               ========================= */}
            <Alert color="info" style={{ marginBottom: 18 }}>
              <b>Urutan Pengisian (biar nggak nyasar):</b><br/>
              1) Data Customer → 2) Deal By/Assist By → 3) Metode Penjualan →<br/>
              4) Cari SKU → <b>Klik Add Item</b> → 5) Isi Final Price → 6) Kurir/Pickup → 7) Metode Pembayaran
            </Alert>

            {/* =========================
                1) CUSTOMER
               ========================= */}
            <BlackSectionHeader
              title="DATA CUSTOMER"
              subtitle={<>⚠️ Semua data di bawah ini adalah data <b>PEMBELI</b>, bukan data Sales.</>}
            />

            <SectionTitle
              title="Customer"
              subtitle={<>Pilih apakah customer sudah pernah terdaftar di sistem atau customer baru.</>}
            />

            <ToggleField
              type='checkbox'
              name='use_existing_user'
              label='Customer sudah terdaftar di sistem?'
              onChange={() => {
                if(formikBag.values.use_existing_user){
                  // switch to NEW customer
                  formikBag.setFieldValue('use_existing_user', false)

                  formikBag.setFieldValue('user', null)
                  formikBag.setFieldValue('user_id', null)
                  formikBag.setFieldValue('email', "")
                  formikBag.setFieldValue('password', "")
                  formikBag.setFieldValue('birthdate', null)

                  formikBag.setFieldValue('first_name', "")
                  formikBag.setFieldValue('last_name', "")
                  formikBag.setFieldValue('name', "")

                  formikBag.setFieldValue('phone_code', null)
                  formikBag.setFieldValue('phone', "")
                  formikBag.setFieldValue('instagram_account', "")
                  formikBag.setFieldValue('lead_source', "")
                  formikBag.setFieldValue('complete_address', "")
                  formikBag.setFieldValue('recipient_name', "")
                  formikBag.setFieldValue('recipient_number', "")
                  formikBag.setFieldValue('instruction', "")
                  formikBag.setFieldValue('latitude', "")
                  formikBag.setFieldValue('longitude', "")

                  // reset selections that depend on customer
                  formikBag.setFieldValue('courier', null)
                  formikBag.setFieldValue('payment_method', null)
                } else {
                  // switch to EXISTING customer
                  formikBag.setFieldValue('use_existing_user', true)
                }
              }}
            />

            <FormText style={{ marginTop: -8, marginBottom: 16 }}>
              • <b>YA</b> = cari customer yang sudah ada<br/>
              • <b>TIDAK</b> = input data customer baru
            </FormText>

            {formikBag.values.use_existing_user && (
              <>
                <UserSelectSearchField
                  name='user'
                  label='Cari Customer (email / nama / nomor HP)'
                  onSelect={(selectedUser) => {
                    const fullName = selectedUser?.name || ''
                    const { first, last } = splitFullName(fullName)

                    formikBag.setFieldValue('user_id', selectedUser.id)
                    formikBag.setFieldValue('email', selectedUser.email)
                    formikBag.setFieldValue('birthdate', selectedUser.birthdate)

                    formikBag.setFieldValue('first_name', first)
                    formikBag.setFieldValue('last_name', last)
                    formikBag.setFieldValue('name', buildFullName(first, last) || fullName)

                    formikBag.setFieldValue('phone_code', selectedUser.phone_code)
                    formikBag.setFieldValue('phone', selectedUser.phone)
                    formikBag.setFieldValue('instagram_account', selectedUser.instagram_account)
                    formikBag.setFieldValue('lead_source', selectedUser.lead_source)
                    formikBag.setFieldValue('complete_address', selectedUser.complete_address)
                    formikBag.setFieldValue('recipient_name', selectedUser.recipient_name)
                    formikBag.setFieldValue('recipient_number', selectedUser.recipient_number)
                    formikBag.setFieldValue('instruction', selectedUser.instruction)
                    formikBag.setFieldValue('latitude', selectedUser.latitude)
                    formikBag.setFieldValue('longitude', selectedUser.longitude)
                  }}
                />

                <ToggleField
                  type="checkbox"
                  name="hide_existing_user_detail"
                  label="Sembunyikan detail customer (biar ringkas)"
                />
                <FormText style={{ marginTop: -8, marginBottom: 16 }}>
                  Jika dimatikan, kamu bisa melihat & mengubah data customer.
                </FormText>
              </>
            )}

            {
              (
                (formikBag.values.use_existing_user && formikBag.values.user_id)
                || addNewUser
              )
              &&
              <>
                {(addNewUser || formikBag.values.hide_existing_user_detail === false) && (
                  <>
                    <InputField
                      type='text'
                      name='email'
                      label='Email Customer'
                      required={ true }
                      formText="⚠️ Pastikan ini email CUSTOMER, bukan email Sales."
                      onBlur={(e) => {
                        if(addNewUser){
                          checkUserEmailIsUnique(e.target.value)
                        }
                      }}
                    />

                    <InputField
                      type='password'
                      name='password'
                      label='Password Customer (opsional)'
                      formText='Jika password tidak diisi, akan diisi random oleh sistem. Password harus 8+ karakter, huruf kecil dan besar, simbol.'
                    />

                    <InputField type='date' label='Tanggal Lahir (opsional)' name='birthdate' />

                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <InputField
                          type='text'
                          name='first_name'
                          label='Nama Depan'
                          onChange={(e) => {
                            const v = e.target.value
                            formikBag.setFieldValue('first_name', v)
                            syncFullNameIntoLegacyName(formikBag, v, formikBag.values.last_name)
                          }}
                        />
                      </div>

                      <div style={{ flex: 1, minWidth: 240 }}>
                        <InputField
                          type='text'
                          name='last_name'
                          label='Nama Belakang'
                          onChange={(e) => {
                            const v = e.target.value
                            formikBag.setFieldValue('last_name', v)
                            syncFullNameIntoLegacyName(formikBag, formikBag.values.first_name, v)
                          }}
                        />
                      </div>
                    </div>

                    <PhoneCodeSelectSearchField
                      name='phone_code'
                      label='Kode Negara (Phone Code)'
                      required={ false }
                      onSelect={(selectedOption) => {
                        formikBag.setFieldValue('phone_code', selectedOption.phone_code)
                        if(addNewUser){
                          checkUserPhoneIsUnique(selectedOption.phone_code + formikBag.values.phone)
                        }
                      }}
                    />
                    <FormText style={{marginTop: "-20px", marginBottom: "20px", fontWeight: "bold"}}>
                      Jika belum ada tanda X, berarti belum dipilih.
                    </FormText>

                    <InputField
                      type='text'
                      name='phone'
                      label='Nomor HP Customer'
                      placeholder="8123456789"
                      formText="Isi nomor HP tanpa spasi/tanda baca. Jangan masukkan phone code (+62)."
                      onBlur={(e) => {
                        const filteredPhone = (e.target.value || "").replace(/\D/g, '')
                        formikBag.setFieldValue('phone', filteredPhone)
                        if(addNewUser){
                          checkUserPhoneIsUnique(formikBag.values.phone_code + filteredPhone)
                        }
                      }}
                    />

                    <InputField
                      type='text'
                      name='instagram_account'
                      label='Instagram Customer (opsional)'
                      placeholder="@username"
                    />

                    <LeadSourceSelectSearchField
                      name='lead_source'
                      label='Customer tahu Belle & Kate dari mana? (opsional)'
                      required={ false }
                    />
                  </>
                )}

                <SectionTitle
                  title="Alamat Pengiriman"
                  subtitle={<>Isi alamat customer. Koordinat membantu menampilkan opsi kurir lebih lengkap.</>}
                />

                <InputField type='text' name='complete_address' label='Alamat Lengkap' />
                <InputField type='text' name='recipient_name' label='Nama Penerima' />
                <InputField
                  type='text'
                  name='recipient_number'
                  label='Nomor Penerima'
                  placeholder="+628123456789"
                  formText="Phone code perlu dimasukkan di sini (contoh: +62...)"
                />
                <InputField type='text' name='instruction' label='Instruksi Tambahan (opsional)' placeholder='Misal: titip resepsionis' />

                {gmapLoaded && !showPinpoint && (
                  <Button color='primary' onClick={() => setShowPinpoint(true)} style={{ marginBottom: 10 }}>
                    { '📍 Pilih Koordinat (opsional)' }
                  </Button>
                )}

                {gmapLoaded && showPinpoint && (
                  <UserAddressPinpoint
                    lat={ parseFloat(formikBag.values.latitude) }
                    long={ parseFloat(formikBag.values.longitude) }
                    setFieldValue={ formikBag.setFieldValue }
                    closePinpoint={ () => { setShowPinpoint(false) } }
                  />
                )}
              </>
            }

            {formikBag.values.use_existing_user && formikBag.values.user_id && (
              <>
                <ToggleField type='checkbox' name='edit_existing_user' label='Simpan perubahan data customer' />
                <FormText style={{ marginTop: -8 }}>
                  Nyalakan hanya jika kamu memang mengubah data customer dan ingin disimpan.
                </FormText>
              </>
            )}

            <hr />

            {/* =========================
                2) DEAL BY / ASSIST BY
               ========================= */}
            <SectionTitle
              title="Deal By / Assist By"
              subtitle={<>• <b>Deal By</b> = Sales yang closing • <b>Assist By</b> = Sales yang membantu</>}
            />

            <SalesAliasSelectSearchField
              name='sales_name_in_invoice'
              label='Nama Sales di Invoice'
              required={ true }
            />

            <SalesNameSelectSearchField
              name='deal_by'
              label='Deal By'
              required={ true }
            />

            <SalesNameSelectSearchField
              name='assist_by'
              label='Assist By'
              required={ true }
            />

            <hr />

            {/* =========================
                3) METODE PENJUALAN
               ========================= */}
            <SectionTitle
              title="Metode Penjualan"
              subtitle={<>Pilih metode penjualan yang paling sesuai dengan konteks deal (misal Regular Purchase).</>}
            />

            <InputField
              type='select'
              name='sales_method'
              label='Metode Penjualan'
              required={ true }
              formText='Pilih Regular Purchase apabila customer membeli diluar event apapun'
            >
              <InputSelectOptions options={ SALES_METHOD } keyPrefix={ 'sales_method' } />
            </InputField>

            <hr />

            {/* =========================
                4) ITEMS (ADD/DELETE)
               ========================= */}
            <SectionTitle
              title="Items"
              subtitle={<>Tambahkan item satu per satu. Setelah Final Price terisi, baru bisa tambah item berikutnya.</>}
            />

            {!canAddNewItem && (
              <Alert color="warning" style={{ marginTop: 10 }}>
                ⚠️ Kamu belum bisa tambah item baru — lengkapi <b>Final Price</b> untuk semua item yang sudah ada.
              </Alert>
            )}

            <Alert color="primary" style={{ marginTop: 10 }}>
              <b>Petunjuk:</b> 1) Cari SKU → 2) Klik <b>➕ Add Item ke Deal</b><br/>
              Kalau cuma pilih SKU tanpa klik Add Item, item belum masuk ke deal.
            </Alert>

            <div style={{ opacity: canAddNewItem ? 1 : 0.6, pointerEvents: canAddNewItem ? 'auto' : 'none' }}>
              <ReactAsyncMultiselectField
                name="product_search"
                label="Cari SKU"
                placeholder="Cari dgn nama atau SKU"
                required={ false }
                loadOptions={ loadOptions }
                getOptionLabel={ getOptionLabel }
                setInputValue={ setProductSearchQuery }
                handleCustomChange={ (options) => {
                  // force single-select: keep only last picked
                  const one = (options || []).slice(-1)
                  formikBag.setFieldValue('product_search', one)
                }}
                dataSelected={ formikBag.values.product_search }
              />

              {hasSelectedButNotAdded && (
                <FormText className="text-warning" style={{ marginTop: 6 }}>
                  ⚠️ SKU sudah dipilih, tapi belum ditambahkan. Klik tombol <b>➕ Add Item ke Deal</b>.
                </FormText>
              )}

              <Button
                color="primary"
                type="button"
                size="lg"
                style={{ marginTop: 10 }}
                disabled={!canClickAdd}
                onClick={() => addSelectedProductAsItem(formikBag)}
              >
                ➕ Add Item ke Deal
              </Button>

              {!selectedToAdd && (
                <FormText className="text-muted" style={{ marginTop: 6 }}>
                  Pilih SKU dulu agar tombol Add Item aktif.
                </FormText>
              )}
            </div>

            <hr />

            {/* =========================
                5) DETAIL ITEMS
               ========================= */}
            <SectionTitle
              title="Detail Items"
              subtitle={<>• <b>Open Price</b> = harga awal (read-only) • <b>Final Price</b> = harga deal setelah nego</>}
            />

            <FormText>
              Metode Penjualan (deal): <b>{formikBag.values.sales_method || '-'}</b>
            </FormText>

            <FieldArray
              name="items"
              render={ (arrayHelpers) => (
                <>
                  {items.length === 0 && (
                    <FormText className="text-muted">
                      Belum ada item. Tambahkan item dari pencarian SKU di atas, lalu klik <b>Add Item</b>.
                    </FormText>
                  )}

                  {items.length > 0 && (
                    <div className="mt-3">
                      {items.map((item, index) => {
                        const product = (formikBag.values.product_ids || []).find(p => p.id === item.product_id)

                        const sku = product?.sku
                        const conditionName = product?.condition_name || '-'
                        const spec = product?.spec || product?.p_size || product?.name || ''
                        const title = product ? `${sku} - ${conditionName} - ${spec}` : `Product #${item.product_id}`

                        const openN = toNumberOrNull(product?.default_price)
                        const finalN = toNumberOrNull(item?.selling_price)
                        const diff = (openN !== null && finalN !== null) ? (openN - finalN) : null

                        return (
                          <div key={item.product_id} className="border rounded p-3 mb-3">
                            <h5 className="mb-3">
                              {index + 1}. {title}
                            </h5>

                            {/* Open Price */}
                            <div className="mb-3">
                              <Label className="form-control-label">Open Price</Label>
                              <Input
                                type="text"
                                value={openN === null ? '-' : StringUtil.currencyCase(openN)}
                                disabled
                              />
                              <FormText>Harga awal dari sistem (tidak bisa diubah)</FormText>
                            </div>

                            {/* Final Price */}
                            <InputField
                              type="number"
                              name={`items.${index}.selling_price`}
                              label="Final Price (Wajib)"
                              placeholder="Masukkan harga deal (nego)"
                              required={ true }
                              formText={
                                finalN === null
                                  ? '⚠️ Isi Final Price agar bisa lanjut tambah item / pilih kurir'
                                  : `Selisih (Open - Final): ${diff === null ? '-' : StringUtil.currencyCase(diff)}`
                              }
                            />

                            <ToggleField
                              type="checkbox"
                              name={`items.${index}.include_authenticity_check`}
                              label="Include Authenticity Check"
                            />

                            <InputField
                              type="text"
                              name={`items.${index}.note`}
                              label="Catatan per Item (opsional)"
                              formText='Isi catatan apapun yang berkaitan dengan product di sini'
                            />

                            <Button
                              color="danger"
                              size="sm"
                              type="button"
                              onClick={() => {
                                // remove item
                                arrayHelpers.remove(index)

                                // keep product_ids in sync
                                const newSelected =
                                  (formikBag.values.product_ids || []).filter(
                                    (p) => p.id !== item.product_id
                                  )
                                formikBag.setFieldValue('product_ids', newSelected)

                                // keep items in sync
                                formikBag.setFieldValue(
                                  'items',
                                  (formikBag.values.items || []).filter((_, i) => i !== index)
                                )

                                // clear dependent selections
                                formikBag.setFieldValue('courier', null)
                                formikBag.setFieldValue('payment_method', null)
                              }}
                            >
                              Delete Item
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )}
            />

            <hr />

            {/* =========================
                6) PICKUP DATE + METODE PENGIRIMAN
               ========================= */}
            <SectionTitle
              title="Pickup Date & Metode Pengiriman"
              subtitle={<>Pickup Date <b>opsional</b>. Kurir wajib dipilih setelah item & Final Price lengkap.</>}
            />

            <DateInputField
              label='Pickup Date (Opsional)'
              name='pickup_at'
              dateFormat="dd/MM/yyyy"
              required={ false }
              formText='📦 Tanggal estimasi barang diambil/dikirim. Boleh dikosongkan jika belum pasti. Format: dd/mm/yy'
            />

            {hasAtLeastOneItem && allItemsComplete ? (
              <CourierSelectSearchField
                name='courier'
                label='Kurir'
                required={ true }
                latitude={formikBag.values.latitude}
                longitude={formikBag.values.longitude}
                productIdWithSellingPriceList={(items || []).map(o => ({
                  product_id: o.product_id,
                  selling_price: parseFloat(o.selling_price),
                }))}
                formText="Tip: isi koordinat alamat agar opsi kurir lebih lengkap."
              />
            ) : (
              <Alert color="secondary">
                Kurir akan muncul setelah:<br/>
                ✔️ Minimal 1 item ditambahkan<br/>
                ✔️ Semua Final Price terisi
              </Alert>
            )}

            <hr />

            {/* =========================
                7) ORDER EXTRAS + NOTE
               ========================= */}
            <SectionTitle
              title="Order"
              subtitle={<>Tambahan order & catatan internal.</>}
            />

            <ToggleField type="checkbox" name="include_invoice" label="Include Invoice" />
            <ToggleField type="checkbox" name="include_polaroid" label="Include Polaroid" />
            <ToggleField type="checkbox" name="include_love_letter" label="Include Surat Cinta" />

            <InputField
              type='text'
              name='voucher_code'
              label='Kode Voucher (opsional)'
              placeholder='NEW500'
              formText='Isi jika pembelian menggunakan voucher'
            />

            <InputField
              type='text'
              name='note'
              label='Catatan (opsional)'
              placeholder='Catatan'
              formText='Catatan internal untuk order/deal'
            />

            <InputField
              type='number'
              name='down_payment'
              label='Down Payment (opsional)'
              placeholder=''
              formText='Boleh dikosongkan jika pembayaran penuh'
            />

            <hr />

            {/* =========================
                8) PAYMENT METHOD (LAST)
               ========================= */}
            <SectionTitle
              title="Metode Pembayaran"
              subtitle={<>Metode pembayaran baru bisa dipilih setelah kurir dipilih.</>}
            />

            {formikBag.values.courier ? (
              <>
                <PaymentMethodSelectSearchField
                  name='payment_method'
                  label='Metode Pembayaran'
                  required={ true }
                />
                <FormText className="text-muted">
                  Jika metode pembayaran belum muncul, pastikan kurir sudah dipilih.
                </FormText>
              </>
            ) : (
              <Alert color="secondary">
                Pilih <b>Kurir</b> dulu untuk lanjut pilih metode pembayaran.
              </Alert>
            )}

            <br />

            <Button
              color='primary'
              disabled={formikBag.isSubmitting}
              onClick={async () => {
                const errors = await formikBag.validateForm()
                if (Object.keys(errors).length > 0) {
                  alert(`Mohon perbaiki error validasi sebelum submit!\n${JSON.stringify(errors, null, 2)}`)
                } else {
                  formikBag.submitForm()
                }
              }}
            >
              { 'Create Deal' }
            </Button>
          </Form>
        )
      }}
    </Formik>
  )
}

SalesDealsCreateForm.propTypes = propTypes
export default SalesDealsCreateForm
